package com.siriusxm.cmc

import com.siriusxm.cmc.utils.getString
import com.siriusxm.pia.Application
import com.siriusxm.pia.Application.configuration
import com.siriusxm.pia.Configuration
import com.siriusxm.pia.components.currentTimeZoneId
import kotlinx.coroutines.promise
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.encodeToJsonElement
import kotlinx.serialization.json.jsonObject
import kotlin.js.Promise

/**
 * The implementation of the application container, the API for embedded,
 * iframe application modules.
 */
class ApplicationContainer(
    val app: Application
) : CMCContainer {
    val encoder = Json {
        encodeDefaults = true
    }
    private val configJson: JsonObject = encoder
        .encodeToJsonElement<Configuration>(configuration)
        .jsonObject

    override fun navigate(path: String) {
        app.navigate(path)
    }

    override fun navigateWithTitle(path: String, title: String) {
        app.navigateWithTitle(path, title)
    }

    override val notifications: Notifications = app.notifications

    override val viewer: CMCViewer
        get() = object : CMCViewer {
            override val id: String get() = app.viewer.id!!
            override val email: String get() = app.viewer.email!!
            override val groups: Array<String>
                get() = app.viewer.groups.toTypedArray()
            override val accessToken: String
                get() = app.viewer.accessToken!!

            override fun refresh(): Promise<String> {
                return app.scope.promise {
                    app.cognito.refresh()
                    app.cognito.accessToken!!
                }
            }
        }

    override val audioPlayer: CMCAudioPlayer = object : CMCAudioPlayer {
        override fun play(
            url: String,
            title: String?,
            subtitle: String?,
            imageUrl: String?
        ) {
            app.audioPlayer.play(url, title, subtitle, imageUrl)
        }

        override fun playEntity(entityId: String): Promise<Unit> = app.scope.promise {
            app.playEntity(entityId)
        }

        override fun playDetails(
            type: String,
            url: String,
            title: String?,
            subtitle: String?,
            imageUrl: String?,
            durationMilliseconds: Double?,
        ): Promise<Unit> = app.scope.promise {
            app.playDetails(type, url, title, subtitle, imageUrl, durationMilliseconds?.toLong())
        }

        override fun close() {
            app.audioPlayer.controller.stop()
        }
    }

    override fun configProperty(key: String): String? {
        return configJson.getString(key)
    }

    // The zone chosen via the global header selector (shared app-wide state, persisted to
    // localStorage). Embedded apps read this from window.parent.cmc.timeZoneId. The parent
    // hard-reloads the page when the selection changes, so this is stable per page load.
    override val timeZoneId: String
        get() = currentTimeZoneId()
}
